import { CircularProgress } from "@mui/material";
import { useState } from "react";
import { t } from "../../translations";
import { createLogger } from "../../utils/logger";
import { POST } from "../constants/request-type";
import { service_api } from "../constants/url";
import { MakeRequest } from "../functions/axios";
import { decryptSingleData } from "../functions/cryptojs";
import "../styles/section-chef.css";
import { openNotificationWithIcon } from "./notification-bar";

const logger = createLogger("HubRequestForm");
const HubRequestForm = ({ user, lang, onSuccess }) => {
  const [formData, setFormData] = useState({
    reason: "",
    additionalInfo: "",
    contactPreferences: {
      email: true,
      phone: true
    }
  });

  const [load, setLoad] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleContactPreferenceChange = (contactType) => {
    setFormData((prev) => ({
      ...prev,
      contactPreferences: {
        ...prev.contactPreferences,
        [contactType]: !prev.contactPreferences[contactType]
      }
    }));
  };

  // No need to initialize service area - will be derived from user location

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.reason) {
      return openNotificationWithIcon(
        "error",
        t("hub_request.required_fields_error", lang)
      );
    }

    setLoad(true);

    const request_details = {
      type: POST,
      url: service_api,
      route: "hub-request/create",
      data: formData,
      token: user?.refresh_token
    };

    try {
      const response = await MakeRequest(request_details);
      const decryptedData = decryptSingleData(response.data.hubRequest);

      openNotificationWithIcon(
        "success",
        t("hub_request.submit_success", lang)
      );

      if (onSuccess) {
        onSuccess(decryptedData);
      }

      // Reset form
      setFormData({
        reason: "",
        additionalInfo: "",
        contactPreferences: {
          email: true,
          phone: true
        }
      });
    } catch (error) {
      logger.error("Hub request submission error:", error);
      openNotificationWithIcon(
        "error",
        error?.response?.data?.message || t("hub_request.submit_error", lang)
      );
    } finally {
      setLoad(false);
    }
  };

  return (
    <div className="section-chef-menu-right-address">
      <div className="section-chef-menu-right-address-inside">
        <div>
          <p
            style={{
              textAlign: "left",
              display: "flex",
              alignItems: "center",
              gap: 15,
              fontWeight: 600,
              marginTop: 30
            }}
          >
            {t("hub_request.title", lang)}
          </p>
          <p
            style={{
              textAlign: "left",
              fontSize: 18
            }}
          >
            {t("hub_request.subtitle", lang)}
          </p>

          <form onSubmit={handleSubmit}>
            <div className="section-chef-menu-right-address-inside-box">
              {/* Reason */}
              <div style={{ width: "100%", marginTop: "20px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "bold"
                  }}
                >
                  <span style={{ color: "red" }}>*</span>{" "}
                  {t("hub_request.reason", lang)}
                </label>
                <select
                  name="reason"
                  value={formData.reason}
                  onChange={handleInputChange}
                  style={{
                    width: "100%",
                    padding: "12px",
                    fontSize: "16px",
                    border: "2px solid #ddd",
                    borderRadius: "5px",
                    outline: "none",
                    transition: "border-color 0.3s",
                    boxSizing: "border-box"
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#ff114d";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#ddd";
                  }}
                >
                  <option value="">
                    {t("hub_request.select_reason", lang)}
                  </option>
                  <option value="no_hubs_nearby">
                    {t("hub_request.reason_no_hubs", lang)}
                  </option>
                  <option value="existing_hub_too_far">
                    {t("hub_request.reason_too_far", lang)}
                  </option>
                  <option value="business_expansion">
                    {t("hub_request.reason_expansion", lang)}
                  </option>
                  <option value="better_location">
                    {t("hub_request.reason_better_location", lang)}
                  </option>
                  <option value="other">
                    {t("hub_request.reason_other", lang)}
                  </option>
                </select>
              </div>

              {/* Additional Info */}
              <div style={{ width: "100%", marginTop: "20px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "bold"
                  }}
                >
                  {t("hub_request.additional_info", lang)}
                </label>
                <textarea
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleInputChange}
                  placeholder={t(
                    "hub_request.additional_info_placeholder",
                    lang
                  )}
                  rows={3}
                  maxLength={300}
                  style={{
                    width: "100%",
                    padding: "12px",
                    fontSize: "16px",
                    border: "2px solid #ddd",
                    borderRadius: "5px",
                    outline: "none",
                    transition: "border-color 0.3s",
                    boxSizing: "border-box",
                    resize: "vertical"
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#ff114d";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#ddd";
                  }}
                />
                <div
                  style={{ fontSize: "12px", color: "#666", marginTop: "5px" }}
                >
                  {formData.additionalInfo.length}/300{" "}
                  {t("hub_request.characters", lang)}
                </div>
              </div>

              {/* Contact Preferences */}
              <div style={{ width: "100%", marginTop: "20px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "bold"
                  }}
                >
                  {t("hub_request.contact_preferences", lang)}
                </label>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px"
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      cursor: "pointer"
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={formData.contactPreferences.email}
                      onChange={() => handleContactPreferenceChange("email")}
                      style={{ transform: "scale(1.2)" }}
                    />
                    <span>{t("hub_request.contact_email", lang)}</span>
                  </label>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      cursor: "pointer"
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={formData.contactPreferences.phone}
                      onChange={() => handleContactPreferenceChange("phone")}
                      style={{ transform: "scale(1.2)" }}
                    />
                    <span>{t("hub_request.contact_phone", lang)}</span>
                  </label>
                </div>
                <div
                  style={{ fontSize: "12px", color: "#666", marginTop: "5px" }}
                >
                  {t("hub_request.contact_preferences_note", lang)}
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div
              className="btn-get-started"
              style={{
                backgroundColor: "#ff114d",
                width: "100%",
                marginTop: "20px"
              }}
              onClick={handleSubmit}
            >
              {!load && t("hub_request.submit_button", lang)}
              {load && <CircularProgress size={25} />}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default HubRequestForm;
